import { FeatureFlagIdCopilotA2Ui } from 'c1gen/features'
import { TimelineItem, getTimestampMs } from 'components/core/copilot/types'
import { useFeatureChecker } from 'components/no-ui/capability'
import { ChatStatus, useChat } from 'components/pages/conversations/hooks/useChat'
import { useAlert } from 'hooks/useAlert'
import { useAutoScroll } from 'hooks/useAutoScroll'
import { SurfaceState, useA2UI } from 'lib/a2ui'
import { submitA2UIAction, useA2UIClient } from 'lib/actions/a2ui'
import { Status, useOAuth } from 'lib/rpc'
import { SurfaceLifecycleState } from 'pbts/c1/api/a2ui/v1/surface'
import { AgentConversationType, UIMessage, UIMessage_MessageType, UIMessageRole } from 'pbts/c1/api/conversations/v1/conversations'
import { Struct } from 'pbts/google/protobuf/struct'
import { Timestamp } from 'pbts/google/protobuf/timestamp'
import { RefObject, useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { createConversationApi, sendMessageApi } from 'redux_toolkit/conversations'

export type UseChatAssistantConfig = {
    /** The conversation type for this assistant */
    conversationType: AgentConversationType
    /** Title for the conversation (used when creating) */
    conversationTitle: string
    /** Optional metadata to include when creating the conversation */
    metadata?: Record<string, unknown>
    /** Error message ID to show when conversation creation fails */
    errorMessageId?: string
    /** Whether to enable A2UI features (default: true if FF enabled) */
    enableA2UI?: boolean
}

export type UseChatAssistantResult = {
    // Conversation state
    conversationId: string | null
    messages: UIMessage[]
    status: ChatStatus
    isStarting: boolean
    isReady: boolean

    // Timeline (messages + surfaces merged and sorted)
    timelineItems: TimelineItem[]

    // Input management
    input: string
    setInput: React.Dispatch<React.SetStateAction<string>>

    // Actions
    handleStart: (message: string) => Promise<void>
    handleSubmit: () => void
    setMessages: React.Dispatch<React.SetStateAction<UIMessage[]>>
    forceReady: () => void

    // A2UI
    hasA2UI: boolean
    surfaces: Map<string, SurfaceState>
    activeSurface: SurfaceState | undefined
    updateDataModel: (surfaceId: string, path: string, value: unknown) => void
    handleSurfaceAction: (
        surfaceId: string,
        actionName: string,
        context: Record<string, string>,
        sourceComponentId: string,
        localHandler?: (actionName: string, context: Record<string, string>, surfaceState: SurfaceState) => boolean | void,
    ) => boolean | void
    handleSurfaceDataChange: (surfaceId: string, path: string, value: unknown) => void

    // Scroll ref
    messagesEndRef: RefObject<HTMLDivElement | null>
}

/**
 * Normalize action names to handle LLM variations.
 * Matches backend NormalizeAction exactly.
 * Examples: apply_cel -> applycel, ApplyCEL -> applycel, apply-cel -> applycel
 */
const normalizeActionName = (action: string): string =>
    action.toLowerCase().replace(/[^a-z]/g, '')

/**
 * Shared hook for chat assistant components. Combines common logic for:
 * - Creating conversations
 * - Sending messages
 * - Managing WebSocket subscriptions
 * - A2UI surface management
 * - Auto-scrolling
 */
export function useChatAssistant(config: UseChatAssistantConfig): UseChatAssistantResult {
    const {
        conversationType,
        conversationTitle,
        metadata,
        errorMessageId = 'copilot_conversation_start_error',
        enableA2UI = true,
    } = config

    const authClient = useOAuth()
    const a2uiClient = useA2UIClient()
    const toast = useAlert()
    const intl = useIntl()

    const [conversationId, setConversationId] = useState<string | null>(null)
    const [isStarting, setIsStarting] = useState(false)

    const hasA2UIFeature = useFeatureChecker(FeatureFlagIdCopilotA2Ui)
    const hasA2UI = enableA2UI && (hasA2UIFeature ?? false)

    const { mutate: createConversation, isReady: isConversationApiReady } = createConversationApi()
    const { mutate: sendMessage } = sendMessageApi()

    // Use existing useChat hook once we have a conversation
    const chat = useChat({
        conversationId: conversationId ?? undefined,
        authClient,
    })

    // A2UI surfaces subscription - force chat ready when surfaces are received
    const { surfaces, updateDataModel } = useA2UI({
        conversationId: hasA2UI ? (conversationId ?? undefined) : undefined,
        onSurfaceReceived: chat.forceReady,
    })

    // Auto-scroll to bottom when messages or surfaces change
    const scrollDeps = [...chat.messages, surfaces.size] as const
    const messagesEndRef = useAutoScroll(scrollDeps)

    // Ref to access current input without causing re-renders in callbacks
    const inputRef = useRef(chat.input)
    inputRef.current = chat.input

    /**
     * Start a new conversation with the first message.
     */
    const handleStart = useCallback(async (message: string): Promise<void> => {
        if (isStarting) return
        if (!isConversationApiReady) {
            console.warn('useChatAssistant: API not ready yet, cannot start conversation')
            return
        }
        setIsStarting(true)

        try {
            const response = await createConversation({
                title: conversationTitle,
                description: '',
                type: conversationType,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ...(metadata && { metadata: Struct.fromJson(metadata as any) }),
            })

            const newConversationId = response.data?.id
            if (!newConversationId) {
                throw new Error('Failed to create conversation')
            }

            setConversationId(newConversationId)

            await sendMessage({
                conversationId: newConversationId,
                message,
            })
        } catch (err) {
            console.error('useChatAssistant: Failed to start conversation', err, { metadata, conversationType })
            toast(
                intl.formatMessage({
                    id: errorMessageId,
                    defaultMessage: 'Failed to start conversation',
                }),
                'error',
            )
        } finally {
            setIsStarting(false)
        }
    }, [
        conversationType,
        conversationTitle,
        metadata,
        createConversation,
        sendMessage,
        isStarting,
        isConversationApiReady,
        intl,
        toast,
        errorMessageId,
    ])

    /**
     * Handle message submission - start new conversation or continue existing.
     */
    const handleSubmit = useCallback((): void => {
        const message = inputRef.current.trim()
        if (!message) return

        if (!conversationId) {
            chat.setInput('')
            handleStart(message)
        } else {
            chat.handleSubmit()
        }
    }, [conversationId, chat, handleStart])

    /**
     * Get the active A2UI surface (latest non-deleted surface by timestamp).
     */
    const activeSurface = useMemo((): SurfaceState | undefined => {
        if (!hasA2UI) return undefined
        let latest: SurfaceState | undefined
        let latestTime = 0
        for (const [, surfaceState] of surfaces) {
            if (surfaceState.surface.state === SurfaceLifecycleState.DELETED) {
                continue
            }
            const createdAt = surfaceState.surface.createdAt
            const surfaceTime = createdAt ? Number(createdAt.seconds) * 1000 + createdAt.nanos / 1000000 : 0
            if (!latest || surfaceTime > latestTime) {
                latest = surfaceState
                latestTime = surfaceTime
            }
        }
        return latest
    }, [hasA2UI, surfaces])

    /**
     * Build timeline of messages and surfaces sorted by timestamp.
     * - Filters assistant messages when A2UI surfaces are present (surfaces replace them)
     * - Skips THINKING messages (handled separately by ChatPanel)
     */
    const timelineItems = useMemo((): TimelineItem[] => {
        const items: TimelineItem[] = []

        // Add messages
        for (const msg of chat.messages) {
            // In A2UI mode, hide assistant responses (surfaces replace them)
            if (hasA2UI && surfaces.size > 0 && msg.role === UIMessageRole.UI_MESSAGE_ROLE_ASSISTANT) {
                continue
            }
            // Skip thinking messages - handled separately by ChatPanel
            if (msg.type === UIMessage_MessageType.THINKING) {
                continue
            }
            items.push({
                type: 'message',
                id: msg.id || `msg-${items.length}`,
                ts: getTimestampMs(msg.createdAt),
                data: msg,
            })
        }

        // Add non-deleted surfaces
        if (hasA2UI) {
            for (const [surfaceId, surfaceState] of surfaces) {
                if (surfaceState.surface.state === SurfaceLifecycleState.DELETED) {
                    continue
                }
                items.push({
                    type: 'surface',
                    id: surfaceId,
                    ts: getTimestampMs(surfaceState.surface.createdAt),
                    data: surfaceState,
                })
            }
        }

        // Sort by timestamp
        items.sort((a, b) => a.ts - b.ts)
        return items
    }, [chat.messages, surfaces, hasA2UI])

    /**
     * Handle A2UI action submission with optional local handler.
     * Normalizes action names before passing to local handler for consistent matching.
     * @param surfaceId - The surface ID
     * @param actionName - The action name (will be normalized before passing to localHandler)
     * @param context - Action context
     * @param sourceComponentId - The component that triggered the action
     * @param localHandler - Optional handler for local-only actions (e.g., apply, copy).
     *                       Receives normalized action name (e.g., 'applycel' not 'apply_cel').
     */
    const handleSurfaceAction = useCallback((
        surfaceId: string,
        actionName: string,
        context: Record<string, string>,
        sourceComponentId: string,
        localHandler?: (actionName: string, context: Record<string, string>, surfaceState: SurfaceState) => boolean | void,
    ): boolean | void => {
        if (!a2uiClient || !conversationId) return

        const surfaceState = surfaces.get(surfaceId)
        if (!surfaceState) return

        // Normalize action name for consistent matching across LLM variations
        const normalizedAction = normalizeActionName(actionName)

        // Call local handler if provided. If it returns true, skip backend submission.
        const handledLocally = localHandler?.(normalizedAction, context, surfaceState)
        if (handledLocally) {
            return true
        }

        // Submit action to backend
        void submitA2UIAction(a2uiClient, {
            conversationId,
            surfaceId,
            sourceComponentId,
            actionName,
            context,
            clientTimestamp: Timestamp.now(),
            dataModelJson: JSON.stringify(surfaceState.dataModel),
        }).then((result) => {
            if (result.status === Status.OK && !result.item.accepted && result.item.errorMessage) {
                updateDataModel(surfaceId, '/error', result.item.errorMessage)
                updateDataModel(surfaceId, '/can_retry', true)
            }
        }).catch((err) => {
            const errorMessage = err instanceof Error ? err.message : String(err)
            updateDataModel(surfaceId, '/error', errorMessage)
            updateDataModel(surfaceId, '/can_retry', true)
        })
    }, [a2uiClient, conversationId, surfaces, updateDataModel])

    /**
     * Handle local data model changes from A2UI input components.
     */
    const handleSurfaceDataChange = useCallback((
        surfaceId: string,
        path: string,
        value: unknown,
    ): void => {
        updateDataModel(surfaceId, path, value)
    }, [updateDataModel])

    // Determine status - prioritize isStarting over chat status
    const status: ChatStatus = isStarting ? 'thinking' : chat.status

    return {
        conversationId,
        messages: chat.messages,
        status,
        isStarting,
        isReady: isConversationApiReady ?? false,
        timelineItems,

        input: chat.input,
        setInput: chat.setInput,

        handleStart,
        handleSubmit,
        setMessages: chat.setMessages,
        forceReady: chat.forceReady,

        hasA2UI,
        surfaces,
        activeSurface,
        updateDataModel,
        handleSurfaceAction,
        handleSurfaceDataChange,

        messagesEndRef,
    }
}
